import { initializeUnderwritingQuestion } from "#src/composables/underwriting-question.composable.ts";
import {
  type RawUnderwritingQuestion,
  type UnderwritingQuestion,
} from "#src/composables/underwriting-question.composable.ts";

import { httpClient } from "#src/HttpClient.ts";
import { useEappStore } from "#src/stores/electronic-application.ts";
import { useInstanceSettingsStore } from "#src/stores/instance-settings.ts";

import { type Pinia } from "pinia";

export class QuestionsService {
  #pinia;
  #baseUrl;
  #httpClient;

  constructor(pinia: Pinia) {
    this.#pinia = pinia;
    const instance = useInstanceSettingsStore(pinia);
    this.#baseUrl = `${instance.api_url}/strife/v1/questions`;
    this.#httpClient = httpClient(pinia);
  }

  #attachEappData(params: object) {
    const { id, uuid } = useEappStore(this.#pinia);
    return {
      electronic_application_id: id,
      uuid: uuid,
      ...params,
    };
  }

  updateUnderwritingQuestion(
    questionId: UnderwritingQuestion["id"],
    body: object
  ) {
    const params = this.#attachEappData(body);
    return this.#httpClient.put(`${this.#baseUrl}/${questionId}`, params);
  }

  async getUnderwritingQuestions() {
    const { id, uuid } = useEappStore(this.#pinia);

    const params = {
      electronic_application_id: id,
      uuid,
    };
    const { data } = await this.#httpClient.get(this.#baseUrl, { params });
    const insuredQuestions: RawUnderwritingQuestion[] = [];
    const jointInsuredQuestions: RawUnderwritingQuestion[] = [];

    data.questions.forEach((q: RawUnderwritingQuestion) => {
      if (q.insured_index === 0) insuredQuestions.push(q);
      if (q.insured_index === 1) jointInsuredQuestions.push(q);
    });

    return {
      insuredQuestions: this.#generateFlatUnderwritingList(insuredQuestions),
      jointInsuredQuestions: this.#generateFlatUnderwritingList(
        jointInsuredQuestions
      ),
    };
  }

  async getTiaUnderwritingQuestions() {
    const { id, uuid } = useEappStore(this.#pinia);
    const params = {
      electronic_application_id: id,
      uuid,
      tia: true,
    };

    const { data } = await this.#httpClient.get(this.#baseUrl, {
      params,
    });

    const { questions, is_eligible } = data;
    const jointInsuredQuestions =
      questions?.filter(
        (v: RawUnderwritingQuestion) => v.insured_index === 1
      ) || [];

    const insuredQuestions =
      questions?.filter(
        (v: RawUnderwritingQuestion) => v.insured_index === 0
      ) ||
      questions ||
      [];

    return {
      jointInsuredQuestions: this.#generateFlatUnderwritingList(
        jointInsuredQuestions
      ),
      insuredQuestions: this.#generateFlatUnderwritingList(insuredQuestions),
      canUseTia: Boolean(is_eligible),
    };
  }

  #generateFlatUnderwritingList(
    questions: RawUnderwritingQuestion[]
  ): Record<string, UnderwritingQuestion> {
    const accumulator: Record<string, UnderwritingQuestion> = {};

    const addToObj = (
      values: RawUnderwritingQuestion[],
      parentId: string | null = null
    ) => {
      values.forEach(rawQuestion => {
        const question = initializeUnderwritingQuestion(rawQuestion, {
          parentId,
        });
        accumulator[question.questionId] = question;
        if (rawQuestion.children?.length) {
          addToObj(rawQuestion.children, question.questionId);
        }
      });
    };

    addToObj(questions);

    return accumulator;
  }
}
